import React, { useState, useMemo } from 'react';
import {
  Search, Database, ChevronDown, ChevronRight, Tag,
  Layers, Box, BarChart3, Grid3X3,
} from 'lucide-react';
import {
  TABLES, EVENT_TYPES, DOMAIN_COLORS, totalColumns,
  type TableSchema, type EventType, type Column,
} from '../data/schema';

type Tab = 'events' | 'bronze' | 'dimensions' | 'gold';
type DomainFilter = 'all' | 'casino' | 'sport' | 'finance' | 'player' | 'reference' | 'aggregation';

const TABS: { key: Tab; label: string; icon: React.ElementType }[] = [
  { key: 'events', label: 'Event Types', icon: Tag },
  { key: 'bronze', label: 'Bronze Tables', icon: Layers },
  { key: 'dimensions', label: 'Dimensions', icon: Grid3X3 },
  { key: 'gold', label: 'Gold Tables', icon: BarChart3 },
];

const DOMAIN_FILTERS: DomainFilter[] = ['all', 'casino', 'sport', 'finance', 'player', 'reference', 'aggregation'];

function DomainBadge({ domain }: { domain: string }) {
  const colors = DOMAIN_COLORS[domain] || DOMAIN_COLORS.reference;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colors.bg} ${colors.text} border ${colors.border}`}>
      {domain}
    </span>
  );
}

function ColumnTable({ columns }: { columns: Column[] }) {
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/80">
          <tr>
            <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Column</th>
            <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Type</th>
            <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
          {columns.map((col) => (
            <tr key={col.name} className="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <td className="px-4 py-2 font-mono text-xs text-accent dark:text-accent-300">{col.name}</td>
              <td className="px-4 py-2 font-mono text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap">
                {col.type}
                {col.nullable && <span className="ml-1 text-slate-400 dark:text-slate-600">?</span>}
              </td>
              <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">{col.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableCard({ table }: { table: TableSchema }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-start justify-between text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <Database className="h-4 w-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
            <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white">{table.name}</span>
            <DomainBadge domain={table.domain} />
            <span className="text-xs text-slate-400 dark:text-slate-500">{table.engine}</span>
          </div>
          <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{table.description}</p>
          {(table.partitionBy || table.orderBy || table.ttl) && (
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500 dark:text-slate-500">
              {table.partitionBy && <span>Partition: <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700 dark:bg-slate-700 dark:text-slate-300">{table.partitionBy}</code></span>}
              {table.orderBy && <span>Order: <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700 dark:bg-slate-700 dark:text-slate-300">{table.orderBy}</code></span>}
              {table.ttl && <span>TTL: <code className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700 dark:bg-slate-700 dark:text-slate-300">{table.ttl}</code></span>}
            </div>
          )}
        </div>
        <div className="ml-4 flex items-center gap-2 flex-shrink-0">
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">{table.columns.length} cols</span>
          {expanded
            ? <ChevronDown className="h-4 w-4 text-slate-400 dark:text-slate-500" />
            : <ChevronRight className="h-4 w-4 text-slate-400 dark:text-slate-500" />
          }
        </div>
      </button>
      {expanded && <ColumnTable columns={table.columns} />}
    </div>
  );
}

function EventCard({ event }: { event: EventType }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-start justify-between text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <Tag className="h-4 w-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
            <span className="font-mono text-sm font-semibold text-slate-900 dark:text-white">{event.name}</span>
            <DomainBadge domain={event.domain} />
          </div>
          <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-400">{event.description}</p>
        </div>
        <div className="ml-4 flex items-center gap-2 flex-shrink-0">
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">{event.payloadFields.length} fields</span>
          {expanded
            ? <ChevronDown className="h-4 w-4 text-slate-400 dark:text-slate-500" />
            : <ChevronRight className="h-4 w-4 text-slate-400 dark:text-slate-500" />
          }
        </div>
      </button>
      {expanded && <ColumnTable columns={event.payloadFields} />}
    </div>
  );
}

export const DataDictionary: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('events');
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all');

  const filteredTables = useMemo(() => {
    const categoryMap: Record<Tab, TableSchema['category'] | null> = {
      events: null, bronze: 'bronze', dimensions: 'dimension', gold: 'gold',
    };
    const category = categoryMap[activeTab];
    if (!category) return [];
    return TABLES.filter((t) => {
      if (t.category !== category) return false;
      if (domainFilter !== 'all' && t.domain !== domainFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return t.name.toLowerCase().includes(q) ||
          t.columns.some((c) => c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q));
      }
      return true;
    });
  }, [activeTab, search, domainFilter]);

  const filteredEvents = useMemo(() => {
    if (activeTab !== 'events') return [];
    return EVENT_TYPES.filter((e) => {
      if (domainFilter !== 'all' && e.domain !== domainFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return e.name.toLowerCase().includes(q) ||
          e.payloadFields.some((f) => f.name.toLowerCase().includes(q) || f.description.toLowerCase().includes(q));
      }
      return true;
    });
  }, [activeTab, search, domainFilter]);

  const kafkaCount = TABLES.filter((t) => t.category === 'kafka').length;
  const bronzeCount = TABLES.filter((t) => t.category === 'bronze').length;
  const dimCount = TABLES.filter((t) => t.category === 'dimension').length;
  const goldCount = TABLES.filter((t) => t.category === 'gold').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Data Dictionary</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">
          Complete schema reference for the bs-reporting ClickHouse data warehouse
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {[
          { label: 'Event Types', value: EVENT_TYPES.length, icon: Tag },
          { label: 'Kafka Tables', value: kafkaCount, icon: Box },
          { label: 'Bronze Tables', value: bronzeCount, icon: Layers },
          { label: 'Dimensions', value: dimCount, icon: Grid3X3 },
          { label: 'Gold Tables', value: goldCount, icon: BarChart3 },
        ].map((stat) => (
          <div key={stat.label} className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-2">
              <stat.icon className="h-4 w-4 text-slate-400 dark:text-slate-500" />
              <span className="text-sm text-slate-500 dark:text-slate-400">{stat.label}</span>
            </div>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Search + Domain Filters */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="relative flex-1 min-w-[200px]">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Search</label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tables, columns, events..."
              className="w-full rounded-md border border-slate-300 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:placeholder-slate-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Domain</label>
          <div className="mt-1 flex flex-wrap gap-1">
            {DOMAIN_FILTERS.map((d) => (
              <button
                key={d}
                onClick={() => setDomainFilter(d)}
                className={`rounded-md px-3 py-2 text-xs font-medium capitalize transition-colors ${
                  domainFilter === d
                    ? 'bg-accent text-white'
                    : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800/50">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-slate-600 hover:bg-white hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-white'
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="space-y-3">
        {activeTab === 'events' ? (
          filteredEvents.length > 0 ? (
            filteredEvents.map((e) => <EventCard key={e.name} event={e} />)
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
              <p className="text-sm text-slate-500">No events match your search.</p>
            </div>
          )
        ) : filteredTables.length > 0 ? (
          filteredTables.map((t) => <TableCard key={t.name} table={t} />)
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
            <p className="text-sm text-slate-500">No tables match your search.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <p className="text-center text-xs text-slate-400 dark:text-slate-600">
        {TABLES.length} tables &middot; {totalColumns} columns &middot; {EVENT_TYPES.length} event types
      </p>
    </div>
  );
};
